/**
 * Business-hours configuration mirror for the frontend.
 *
 * Logic must stay byte-identical with the Deno helper at
 * supabase/functions/_shared/business-hours.ts. Edge functions can't import
 * from src/, so we duplicate. If you change one, change the other.
 */
import {
  fromZonedTime,
  toZonedTime,
  formatInTimeZone,
} from 'date-fns-tz';

export interface BusinessHoursConfig {
  /** IANA timezone name, e.g. "America/New_York". */
  tz: string;
  /** ISO-8601 weekdays the campaign may dispatch on (1=Mon … 7=Sun). */
  days: number[];
  /** Allowed daily windows, in minutes since local midnight. 0 ≤ start < end ≤ 1440. */
  windows: { start: number; end: number }[];
}

export type ValidationResult =
  | { ok: true; value: BusinessHoursConfig }
  | { ok: false; errors: string[] };

const DAY_VALUES = [1, 2, 3, 4, 5, 6, 7];
const MAX_LOOKAHEAD_DAYS = 8;

function jsDayToIso(jsDay: number): number {
  return jsDay === 0 ? 7 : jsDay;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

// Format a minute-of-day count as "9:00 AM" / "5:00 PM" / "midnight" (1440).
function fmtMin(min: number): string {
  if (min === 1440) return 'midnight';
  const h = Math.floor(min / 60);
  const m = min % 60;
  const period = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${pad2(m)} ${period}`;
}

function fmtWindow(w: { start: number; end: number }): string {
  return `${fmtMin(w.start)}–${fmtMin(w.end)}`;
}

export function isWithinBusinessHours(
  now: Date,
  cfg: BusinessHoursConfig | null,
): boolean {
  if (!cfg) return true;
  const local = toZonedTime(now, cfg.tz);
  const isoDay = jsDayToIso(local.getDay());
  if (!cfg.days.includes(isoDay)) return false;
  const minute = local.getHours() * 60 + local.getMinutes();
  return cfg.windows.some((w) => minute >= w.start && minute < w.end);
}

export function nextWindowOpenAt(
  anchor: Date,
  cfg: BusinessHoursConfig | null,
): Date | null {
  if (!cfg) return null;

  const localAnchor = toZonedTime(anchor, cfg.tz);
  const baseY = localAnchor.getFullYear();
  const baseM = localAnchor.getMonth();
  const baseD = localAnchor.getDate();

  const sortedWindows = [...cfg.windows].sort((a, b) => a.start - b.start);

  for (let offset = 0; offset < MAX_LOOKAHEAD_DAYS; offset++) {
    const probe = new Date(Date.UTC(baseY, baseM, baseD + offset));
    const y = probe.getUTCFullYear();
    const m = probe.getUTCMonth() + 1;
    const d = probe.getUTCDate();

    const noonUtc = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
    const isoDay = jsDayToIso(toZonedTime(noonUtc, cfg.tz).getDay());
    if (!cfg.days.includes(isoDay)) continue;

    for (const w of sortedWindows) {
      const hh = Math.floor(w.start / 60);
      const mm = w.start % 60;
      const local = `${y}-${pad2(m)}-${pad2(d)}T${pad2(hh)}:${pad2(mm)}:00`;
      const utc = fromZonedTime(local, cfg.tz);
      if (utc.getTime() > anchor.getTime()) return utc;
    }
  }

  return null;
}

export function validateBusinessHours(cfg: unknown): ValidationResult {
  const errors: string[] = [];

  if (cfg === null || cfg === undefined) {
    errors.push('business_hours is null');
    return { ok: false, errors };
  }
  if (typeof cfg !== 'object' || Array.isArray(cfg)) {
    errors.push('business_hours must be an object');
    return { ok: false, errors };
  }

  const c = cfg as Record<string, unknown>;

  let tz: string | null = null;
  if (typeof c.tz !== 'string' || c.tz.length === 0) {
    errors.push('Pick a timezone for the business hours.');
  } else {
    try {
      formatInTimeZone(new Date(), c.tz, 'yyyy');
      tz = c.tz;
    } catch {
      errors.push(`"${c.tz}" is not a valid timezone.`);
    }
  }

  let days: number[] = [];
  if (!Array.isArray(c.days)) {
    errors.push('Days must be a list.');
  } else if (c.days.length === 0) {
    errors.push('Pick at least one day for the campaign to run.');
  } else {
    const seen = new Set<number>();
    let bad = false;
    for (const raw of c.days) {
      if (!Number.isInteger(raw) || !DAY_VALUES.includes(raw as number)) {
        errors.push(`Invalid day value: ${JSON.stringify(raw)} (expected Mon–Sun).`);
        bad = true;
        break;
      }
      seen.add(raw as number);
    }
    if (!bad) days = Array.from(seen).sort((a, b) => a - b);
  }

  let windows: { start: number; end: number }[] = [];
  if (!Array.isArray(c.windows)) {
    errors.push('Time windows must be a list.');
  } else if (c.windows.length === 0) {
    errors.push('Add at least one time window.');
  } else {
    const parsed: { start: number; end: number }[] = [];
    for (const w of c.windows) {
      if (
        typeof w !== 'object' || w === null ||
        !Number.isInteger((w as Record<string, unknown>).start) ||
        !Number.isInteger((w as Record<string, unknown>).end)
      ) {
        errors.push('Each time window needs a start and end time.');
        continue;
      }
      const start = (w as { start: number }).start;
      const end = (w as { end: number }).end;
      if (start < 0 || start >= 1440) {
        errors.push(`Start time is out of range.`);
        continue;
      }
      if (end <= 0 || end > 1440) {
        errors.push(`End time is out of range.`);
        continue;
      }
      if (start >= end) {
        errors.push(
          `Window ${fmtMin(start)}–${fmtMin(end)}: end time must be after start time. Cross-midnight windows aren't supported — split into two windows on adjacent days.`,
        );
        continue;
      }
      parsed.push({ start, end });
    }
    parsed.sort((a, b) => a.start - b.start);
    for (let i = 1; i < parsed.length; i++) {
      if (parsed[i].start < parsed[i - 1].end) {
        const a = fmtWindow(parsed[i - 1]);
        const b = fmtWindow(parsed[i]);
        errors.push(
          a === b
            ? `Duplicate time window: ${a}`
            : `Time windows overlap: ${a} and ${b}`,
        );
      }
    }
    windows = parsed;
  }

  if (errors.length > 0 || tz === null || days.length === 0 || windows.length === 0) {
    return { ok: false, errors };
  }

  return { ok: true, value: { tz, days, windows } };
}

/**
 * Format a window in the campaign's timezone for human display.
 * E.g. `formatWindow({start:540,end:1020}, 'America/New_York')` → "9:00 AM - 5:00 PM".
 */
export function formatWindowLocal(w: { start: number; end: number }): string {
  const fmt = (mins: number) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    const period = h < 12 ? 'AM' : 'PM';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${pad2(m)} ${period}`;
  };
  return `${fmt(w.start)} - ${fmt(w.end)}`;
}

const DAY_LABELS = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export function formatDays(days: number[]): string {
  const sorted = [...days].sort((a, b) => a - b);
  // Compact contiguous runs: [1,2,3,4,5] → "Mon-Fri".
  if (sorted.length === 0) return '';
  const runs: number[][] = [[sorted[0]]];
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] === sorted[i - 1] + 1) runs[runs.length - 1].push(sorted[i]);
    else runs.push([sorted[i]]);
  }
  return runs
    .map((r) => r.length === 1 ? DAY_LABELS[r[0]] : `${DAY_LABELS[r[0]]}-${DAY_LABELS[r[r.length - 1]]}`)
    .join(', ');
}
